import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import api from '../../../../utils/api';
import analytics from '../../../../segmentTracking';

const SchoolModal = ({ isOpen, onClose, school, onSchoolSaved }) => {
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [nameError, setNameError] = useState('');
  const [currentUser] = useState(JSON.parse(localStorage.getItem('user')));
  const [currentInstitution] = useState(
    JSON.parse(localStorage.getItem('institution')),
  );

  const toast = useToast();
  const isEdit = !!school;

  // Initialize form when school changes
  useEffect(() => {
    if (school) {
      setName(school.name || '');
    } else {
      setName('');
    }
    setNameError('');
  }, [school, isOpen]);

  // Validate form
  const validateForm = () => {
    let isValid = true;
    setNameError('');

    if (!name.trim()) {
      setNameError('School name is required');
      isValid = false;
    } else if (name.trim().length < 2) {
      setNameError('School name must be at least 2 characters');
      isValid = false;
    }

    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const requestData = {
        name: name.trim(),
        institution: currentInstitution?._id,
      };

      let response;
      let successMessage;

      if (isEdit) {
        // Update existing school
        response = await api.patch(`/dashboard/schools/${school._id}`, {
          name: name.trim(),
        });
        successMessage = 'School updated successfully';

        // Track edit event
        analytics.track('settings:academic:schools:edited', {
          schoolId: school._id,
          oldName: school.name,
          newName: name.trim(),
          userRole: currentUser?.role,
          email: currentUser?.email,
        });
      } else {
        // Create new school
        response = await api.post('/dashboard/schools', requestData);
        successMessage = 'School added successfully';

        // Track add event
        analytics.track('settings:academic:schools:added', {
          schoolName: name.trim(),
          institutionId: currentInstitution?._id,
          userRole: currentUser?.role,
          email: currentUser?.email,
        });
      }

      toast({
        title: 'Success',
        description: successMessage,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Reset form and close modal
      handleClose();
      onSchoolSaved();
    } catch (error) {
      console.error('Error saving school:', error);

      let errorMessage = 'An error occurred';
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.status === 409) {
        errorMessage = 'A school with this name already exists';
      }

      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });

      // Track error event
      analytics.track('settings:academic:schools:error', {
        action: isEdit ? 'edit' : 'add',
        error: errorMessage,
        userRole: currentUser?.role,
        email: currentUser?.email,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    setName('');
    setNameError('');
    onClose();
  };

  // Handle input change
  const handleNameChange = (e) => {
    setName(e.target.value);
    // Clear error when user starts typing
    if (nameError) {
      setNameError('');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit}>
          <ModalHeader>{isEdit ? 'Edit School' : 'Add New School'}</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired isInvalid={!!nameError}>
                <FormLabel fontSize="14px" fontWeight="600" color="#222222">
                  School Name
                </FormLabel>
                <Input
                  value={name}
                  onChange={handleNameChange}
                  placeholder="Enter school name"
                  bg="white"
                  border="1px solid #E7E7E7"
                  borderRadius="8px"
                  _placeholder={{ color: '#757575' }}
                  _focus={{
                    borderColor: '#EF6506',
                    boxShadow: '0 0 0 1px #EF6506',
                  }}
                />
                {nameError && (
                  <Text fontSize="12px" color="red.500" mt={1}>
                    {nameError}
                  </Text>
                )}
              </FormControl>

              <Text fontSize="12px" color="#757575">
                This school will be associated with{' '}
                <strong>{currentInstitution?.name}</strong>
              </Text>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="outline" mr={3} onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              bg="#EF6506"
              color="white"
              _hover={{ bg: '#D55A05' }}
              isLoading={isLoading}
              loadingText={isEdit ? 'Updating...' : 'Adding...'}
            >
              {isEdit ? 'Update School' : 'Add School'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default SchoolModal;
